import { api } from "../services/api";
import type { User } from "../types";

interface AuthContext {
	user: User | null;
	login: (email: string, password: string) => Promise<void>;
	register: (name: string, email: string, password: string) => Promise<void>;
	logout: () => void;
	isLoading: boolean;
}

const AuthContext = createContext<AuthContext | null>(null);

export function useAuth() {
	const context = useContext(AuthContext);
	if (!context) {
		throw new Error("useAuth must be used within AuthProvider");
	}
	return context;
}

interface LoginResponse {
	user: {
		id: string;
		email: string;
		name: string;
		role: "DOCTOR" | "SECRETARY" | "RECEPTIONIST" | "ADMIN"; // Backend usa uppercase
	};
	token: string;
}

export function useAuthState() {
	const [user, setUser] = useState<User | null>(null);
	const [isLoading, setIsLoading] = useState(true);

	// Restaurar sessão do localStorage na inicialização
	useEffect(() => {
		const savedUser = localStorage.getItem("user");
		const savedToken = localStorage.getItem("token");

		if (savedUser && savedToken) {
			try {
				const parsedUser = JSON.parse(savedUser);
				setUser(parsedUser);
			} catch (_error) {
				// Se JSON inválido, limpar localStorage
				localStorage.removeItem("user");
				localStorage.removeItem("token");
			}
		}

		setIsLoading(false);
	}, []);

	const login = async (email: string, password: string) => {
		setIsLoading(true);
		try {
			// Chamar API real de login
			const response = await api.post<LoginResponse>(
				"/api/auth/login",
				{ email, password },
				{ skipAuth: true }, // Rota pública
			);

			// Converter role de uppercase para lowercase (padrão do frontend)
			const user: User = {
				id: response.user.id,
				name: response.user.name,
				email: response.user.email,
				role: response.user.role.toLowerCase() as User["role"],
			};

			// Salvar token e user no localStorage
			localStorage.setItem("token", response.token);
			localStorage.setItem("user", JSON.stringify(user));

			setUser(user);
		} catch (error) {
			console.error("Login error:", error);
			throw error; // Propagar erro para componente mostrar mensagem
		} finally {
			setIsLoading(false);
		}
	};

	const register = async (name: string, email: string, password: string) => {
		setIsLoading(true);
		try {
			// Chamar API real de registro
			const response = await api.post<LoginResponse>(
				"/api/auth/register",
				{ name, email, password },
				{ skipAuth: true }, // Rota pública
			);

			// Converter role de uppercase para lowercase
			const user: User = {
				id: response.user.id,
				name: response.user.name,
				email: response.user.email,
				role: response.user.role.toLowerCase() as User["role"],
			};

			// Salvar token e user no localStorage
			localStorage.setItem("token", response.token);
			localStorage.setItem("user", JSON.stringify(user));

			setUser(user);
		} catch (error) {
			console.error("Register error:", error);
			throw error;
		} finally {
			setIsLoading(false);
		}
	};

	const logout = () => {
		localStorage.removeItem("token");
		localStorage.removeItem("user");
		setUser(null);
	};

	return { user, login, register, logout, isLoading };
}

export { AuthContext };
